import React, { useEffect, useState, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Stack,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Skeleton,
  TablePagination,
  MenuItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Delete,
  AddBox,
} from '@mui/icons-material';
import {
  useCreateItemOpeningStockMutation,
  useListItemOpeningStockQuery,
} from '../../features/StockManagement/apis/stockManagement.api';
import { listAccountOpeningBalanceApi } from '../../redux/apis/accountOpeningBalance.api';
import { dashboardApi } from '../../redux/apis/dashboardApi';
import { API, graphqlOperation } from 'aws-amplify';
import { listItemMasterSearch } from '../../graphql';
import { useGetCompanyCodeTranslationQuery } from '../../redux/apis/getCompanyCodeTranslation.api';
import {
  showErrorSnackbar,
  showSuccessSnackbar,
  showWarningSnackbar,
} from '../snackBar/snakbar';
import { MoneyFormatv3 } from '../../library/MoneyFormat';
import { useDispatch, useSelector } from 'react-redux';
import {
  setBlockedPath,
  setCustomModalAction,
  setModalBlocked,
  setModalOpen,
} from '../../redux/reducers/controllNavigation';
import AddNewItemModal from '../../pages/PrimeFigma/Modals/AddNewItemModal';
import useInfiniteScrollOpeningStock from './UseInfiniteScrollOpeningStock';

function EnterOpeningStock({ data, entryDate, onClose, updateLedger, onSaveOpeningBalance, accountOpeningBalance }) {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [itemMaster, setItemMaster] = useState([]);
  const [page, setPage] = useState(0);
  const [itemMasterFrom, setItemMasterFrom] = useState(0);
  const itemSearchWordRef = useRef('');
  const [autoRowAdded, setAutoRowAdded] = useState(false);
  const [pendingDeletions, setPendingDeletions] = useState([]);
  const [enableEditMode, setEnableEditMode] = useState(false);
  const rowsPerPage = 10;

  const [codeInputMap, setCodeInputMap] = useState({});
  const [nameInputMap, setNameInputMap] = useState({});

  // Add Item modal state
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [addItemRowIndex, setAddItemRowIndex] = useState(null);

  const dispatch = useDispatch();
  const { isCheckModalBlocked } = useSelector((state) => state.controllNavigationSlice);

  const originalRef = useRef([]);

  // Move fetchItemMaster function before hooks that use it
  const fetchItemMaster = async (word, rowfrom, limitno) => {
    try {
      // Store the search word for infinite scroll context
      if (rowfrom === 0) {
        itemSearchWordRef.current = word || '';
      }
      
      const { data } = (await API.graphql(
        graphqlOperation(listItemMasterSearch, {
          word: word || itemSearchWordRef.current || '',
          type: '',
          rowfrom: rowfrom || 0,
          limitno: limitno || 20,
        })
      ));
      
      if (data?.listItemMasterSearch?.length > 0) {
        const newItems = data.listItemMasterSearch.map((item) => ({
          itemid: item.itemid,
          itemcode: item.itemcode,
          itemname: item.itemname,
          measurementcd: item.measurementcd,
          units: item.units,
          is_active: item.is_active
        }));

        if (rowfrom === 0) {
          // First search or new search - replace the list
          setItemMaster(newItems);
          setIsLoading(false);
        } else {
          // Pagination - accumulate results more efficiently for scroll preservation
          setItemMaster((prev) => {
            // Only add items that don't already exist
            const existingIds = new Set(prev.map(item => item.itemid));
            const uniqueNewItems = newItems.filter(item => !existingIds.has(item.itemid));
            
            if (uniqueNewItems.length > 0) {
              return [...prev, ...uniqueNewItems];
            }
            return prev; // No change if no new items
          });
          setIsLoading(false);
        }
      } else {
        if (rowfrom === 0) {
          // No results for search - show empty list
          setItemMaster([]);
        }
        setIsLoading(true);
      }
    } catch (e) {
      setIsLoading(true);
      console.log("error", e);
    }
  };

  // Separate function for fetching specific items during edit operations (avoids infinite scroll conflicts)
  const fetchSpecificItem = async (itemCode, itemName) => {
    try {
      const lookup = itemCode || itemName;
      if (!lookup) return;

      const { data } = await API.graphql(
        graphqlOperation(listItemMasterSearch, {
          word: lookup,
          type: '',
          rowfrom: 0, // Always start from 0 for specific item search
          limitno: 20,
        })
      );
      
      if (data?.listItemMasterSearch?.length > 0) {
        const newItems = data.listItemMasterSearch.map((item) => ({
          itemid: item.itemid,
          itemcode: item.itemcode,
          itemname: item.itemname,
          measurementcd: item.measurementcd,
          units: item.units,
          is_active: item.is_active
        }));

        // Add items to itemMaster without triggering infinite scroll
        setItemMaster((prev) => {
          const existingIds = new Set(prev.map(item => item.itemid));
          const uniqueNewItems = newItems.filter(item => !existingIds.has(item.itemid));
          
          if (uniqueNewItems.length > 0) {
            return [...prev, ...uniqueNewItems];
          }
          return prev;
        });
      }
    } catch (e) {
      console.log("Error fetching specific item:", e);
    }
  };

  // Enhanced function for fetching and merging specific item with default rows during edit operations
  const fetchAndMergeItemData = async (itemCode, itemName) => {
    const lookup = itemCode || itemName;
    try {
      if (!lookup) {
        // If no lookup, just fetch default rows using the regular function
        await fetchItemMaster('', 0, 20);
        return;
      }

      // First, fetch the specific item to ensure it's available
      const specificData = await API.graphql(
        graphqlOperation(listItemMasterSearch, {
          word: lookup,
          type: '',
          rowfrom: 0,
          limitno: 20,
        })
      );

      // Process specific item results
      const specificItems = specificData?.data?.listItemMasterSearch?.length > 0 
        ? specificData.data.listItemMasterSearch.map((item) => ({
            itemid: item.itemid,
            itemcode: item.itemcode,
            itemname: item.itemname,
            measurementcd: item.measurementcd,
            units: item.units,
            is_active: item.is_active
          }))
        : [];

      // Add specific items to itemMaster first
      if (specificItems.length > 0) {
        setItemMaster((prev) => {
          const existingIds = new Set(prev.map(item => item.itemid));
          const uniqueNewItems = specificItems.filter(item => !existingIds.has(item.itemid));
          
          if (uniqueNewItems.length > 0) {
            return [...prev, ...uniqueNewItems];
          }
          return prev;
        });
      }

      // Then fetch default rows using the regular fetchItemMaster function
      // This ensures the infinite scroll pagination works correctly
      await fetchItemMaster('', 0, 20);

    } catch (e) {
      console.log("Error fetching and merging item data:", e);
      // Fallback to regular fetch
      await fetchItemMaster(lookup, 0, 20);
    }
  };


  // Infinite scroll for item listing - now after function definition
  const { setIsLoading, ListboxProps, PaperComponent } = useInfiniteScrollOpeningStock(
    fetchItemMaster,
    itemMasterFrom,
    setItemMasterFrom,
    itemSearchWordRef.current // Pass current search term for pagination
  );

  const {
    data: stockSummaryData,
    error: stockSummaryError,
    isLoading: stockSummaryLoading,
  } = useListItemOpeningStockQuery({ refetchOnMountOrArgChange: true });

  const { data: unitList, isLoading: unitListLoading } =
    useGetCompanyCodeTranslationQuery({ codetype: 'UNITS' });

  const [createItemOpeningStock] = useCreateItemOpeningStockMutation();

  const toNumber = (v) =>
    v === '' || v === null || v === undefined ? 0 : Number(v);
  const round2 = (n) =>
    Math.round((toNumber(n) + Number.EPSILON) * 100) / 100;
  const eq2 = (a, b) => Math.abs(round2(a) - round2(b)) < 0.005;

  const recalculateSerialNumbers = (data) => {
    return data.map((row, index) => ({
      ...row,
      slNo: index + 1,
    }));
  };

  const computeOB = (qty, rate) => round2(toNumber(qty) * toNumber(rate));

  const isEmptyRow = (r) =>
    !r?.itemCode &&
    !r?.itemName &&
    !r?.units &&
    (r?.quantity === null ||
      r?.quantity === undefined ||
      r?.quantity === '' ||
      r?.quantity === 0) &&
    (r?.rate === null || r?.rate === undefined || r?.rate === '' || r?.rate === 0);

  const isCompleteRow = (r) =>
    !!r?.itemid &&
    !!r?.itemCode &&
    !!r?.itemName &&
    !!r?.units &&
    toNumber(r?.quantity) > 0 &&
    toNumber(r?.rate) > 0;

  const hasAnyValues = (r) =>
    !!r?.itemCode ||
    !!r?.itemName ||
    !!r?.units ||
    (r?.quantity !== null && r?.quantity !== undefined && r?.quantity !== '' && r?.quantity !== 0) ||
    (r?.rate !== null && r?.rate !== undefined && r?.rate !== '' && r?.rate !== 0);

  const isRowChanged = (row) => {
    // new row: only "changed" if it's complete
    if (row.new) return isCompleteRow(row);

    const orig = originalRef.current.find((o) => o.key === row.key);
    if (!orig) return isCompleteRow(row);

    const diff =
      (row.itemid || '') !== (orig.itemid || '') ||
      (row.itemCode || '') !== (orig.itemCode || '') ||
      (row.itemName || '') !== (orig.itemName || '') ||
      (row.units || '') !== (orig.units || '') ||
      !eq2(row.quantity, orig.quantity) ||
      !eq2(row.rate, orig.rate);

    return diff;
  };

  const isRowValidForUpdate = (row) => {
    // Check if row has valid item identification
    const hasValidItem = row.itemCode || row.itemName;
    const hasValidQuantity = toNumber(row.quantity) > 0;
    const hasValidRate = toNumber(row.rate) > 0;
    
    return hasValidItem && hasValidQuantity && hasValidRate;
  };


  useEffect(() => {
    fetchItemMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (stockSummaryData && stockSummaryData?.length) {
      setLoading(true);
      const formatted = stockSummaryData.map((it, idx) => ({
        key: `existing-${it.itemid || idx}-${idx}`, // Add index to make unique keys
        slNo: idx + 1,
        itemCode: it.itemcode || '',
        itemName: it.itemname || '',
        units: it.units || '',
        quantity: toNumber(it.stockquantity) || 0,
        rate: toNumber(it.rate_unit) || 0,
        openingBalance: computeOB(it.stockquantity, it.rate_unit),
        isEditable: false,
        isDraft: false,
        new: false,
        itemid: it.itemid || '',
      }));
      
      // Filter out any rows that have missing required fields
      const validFormatted = formatted.filter((row) => {
        const hasValidItem = row.itemCode || row.itemName;
        const hasValidQuantity = toNumber(row.quantity) > 0;
        const hasValidRate = toNumber(row.rate) > 0;
        return hasValidItem && hasValidQuantity && hasValidRate;
      });
      
      originalRef.current = validFormatted.map((r) => ({ ...r }));
      setDataSource(validFormatted); // Don't recalculate here, let the effect handle it
      setLoading(false);
      setAutoRowAdded(false);
    } else if (stockSummaryData && stockSummaryData.length === 0) {
      const newRow = {
        key: `new-${Date.now()}`,
        slNo: 1,
        itemCode: '',
        itemName: '',
        units: '',
        quantity: null,
        rate: null,
        openingBalance: 0,
        isEditable: true,
        isDraft: false,
        new: true,
        itemid: '',
      };
      setDataSource([newRow]);
      setAutoRowAdded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockSummaryData, stockSummaryError]);

  useEffect(() => {
    if (dataSource.some((row) => row.new)) {
      const lastPage = Math.floor((dataSource.length - 1) / rowsPerPage);
      setPage(lastPage >= 0 ? lastPage : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource.length]);

  useEffect(() => {
    // Check for actual changes: only block if there are meaningful changes
    const hasDirty = dataSource.some(
      (row) => {
        // Block if there are drafts (saved changes)
        if (row.isDraft) return true;
        
        // Block if there are pending deletions
        if (pendingDeletions.includes(row.key)) return true;
        
        // Block if there are complete rows that have been changed
        if (!row.new && isRowChanged(row)) return true;
        
        // Block if there are new rows with any values (even incomplete)
        if (row.new && hasAnyValues(row)) return true;
        
        return false;
      }
    );
    
    dispatch(setModalBlocked(hasDirty));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, dispatch]);

  useEffect(() => {
    if (dataSource.length > 0) {
      const needsRecalculation = dataSource.some((row, index) => row.slNo !== index + 1);
      if (needsRecalculation) {
        setDataSource(recalculateSerialNumbers(dataSource));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource.length]);

  useEffect(() => {
    const hasEditableRow = dataSource.some(row => row.isEditable);
    const hasExistingRows = dataSource.some(row => !row.new);
    const hasEmptyEditableRow = dataSource.some(row => isEmptyRow(row) && row.isEditable);
    
    // Only add auto-empty row if we have existing rows, no editable rows, and haven't already added one
    // AND we're not in the initial load phase AND we're in edit mode
    if (hasExistingRows && !hasEditableRow && !hasEmptyEditableRow && !autoRowAdded && dataSource.length > 0 && !loading && enableEditMode) {
      addNewRow(false, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockSummaryData, dataSource.length, loading, enableEditMode]);

  const handleClose = () => {
    if (isCheckModalBlocked) {
      dispatch(setBlockedPath('/openingbalance'));
      dispatch(setModalOpen(true));
      dispatch(setCustomModalAction({ key: 'modalName', value: 'stockModal' }));
      dispatch(setCustomModalAction({ key: 'onLeaveFunction', value: onClose }));
    } else {
      // Clear pending deletions and input maps when closing without saving
      setPendingDeletions([]);
      setCodeInputMap({});
      setNameInputMap({});
      onClose();
    }
  };

  const handleAddItemClick = (rowIndex, fieldType) => {
    setAddItemRowIndex(rowIndex);
    setAddItemModalOpen(true);
  };

  const handleAddItemModalClose = () => {
    setAddItemModalOpen(false);
    setAddItemRowIndex(null);
  };

  const handleItemAdded = async (itemid, rowIndex) => {
    // Check if the item was added successfully and auto-select it
    if (addItemRowIndex !== null && itemid && addItemRowIndex === rowIndex) {
      try {
        // Fetch the newly created item directly without delay
        const { data } = await API.graphql(
          graphqlOperation(listItemMasterSearch, {
            word: '',
            type: '',
            rowfrom: 0,
            limitno: 500 // Fetch enough items to find the new one
          })
        );
        
        if (data?.listItemMasterSearch?.length > 0) {
          const newItem = data.listItemMasterSearch.find(item => item.itemid === itemid);
          if (newItem) {
            const rowKey = dataSource[addItemRowIndex]?.key;
            if (rowKey) {
              // Update the itemMaster state first
              setItemMaster(prev => {
                const existingIds = new Set(prev.map(item => item.itemid));
                if (!existingIds.has(newItem.itemid)) {
                  return [...prev, newItem];
                }
                return prev;
              });
              
              // Immediately populate the row
              handleFieldChange(rowKey, {
                itemCode: newItem.itemcode || '',
                itemName: newItem.itemname || '',
                units: newItem.measurementcd || '',
                itemid: newItem.itemid || '',
              });
              
              // Update input maps instantly
              setCodeInputMap((m) => ({ ...m, [rowKey]: newItem.itemcode }));
              setNameInputMap((m) => ({ ...m, [rowKey]: newItem.itemname }));
            }
          }
        }
      } catch (error) {
        console.error('Error auto-selecting new item:', error);
      }
    }
    handleAddItemModalClose();
  };

  const handleEnableEdit = () => {
    setEnableEditMode(true);
    // Add empty row when entering edit mode with existing items
    const hasEmptyEditableRow = dataSource.some(row => isEmptyRow(row) && row.isEditable);
    if (!hasEmptyEditableRow) {
      addNewRow(false, true);
    }
  };

  const handleFieldChange = (key, fields) => {
    const { itemName, itemCode, quantity, rate } = fields;
    const lookup = itemName ?? itemCode;
    if (lookup) {
      const found = itemMaster.some(
        (im) =>
          (itemName && im.itemname === itemName) ||
          (itemCode && im.itemcode === itemCode)
      );
      if (!found) {
        setItemMasterFrom(0);
        fetchItemMaster(lookup, 0, 20);
      }
    }

    const unitObj = unitList?.find((u) => u.id === Number(fields.units));

    // Only validate quantity and rate if they are being explicitly set to zero or empty
    // Don't block the field change, just show warning
    if (quantity !== undefined && quantity !== null && quantity !== '') {
      const qtyNum = toNumber(quantity);
      if (qtyNum <= 0) {
        // Don't return here, just show warning and allow the change
        showWarningSnackbar('Stock Quantity should be greater than zero for valid entries.');
      }
    }
    if (rate !== undefined && rate !== null && rate !== '') {
      const rateNum = toNumber(rate);
      if (rateNum <= 0) {
        // Don't return here, just show warning and allow the change
        showWarningSnackbar('Rate/unit should be greater than zero for valid entries.');
      }
    }

    setDataSource((src) =>
      src.map((row) =>
        row.key === key
          ? {
              ...row,
              ...fields,
              units: unitObj?.codetext || fields.units,
              quantity:
                fields.quantity !== undefined ? toNumber(fields.quantity) : row.quantity,
              rate: fields.rate !== undefined ? toNumber(fields.rate) : row.rate,
              openingBalance: computeOB(
                fields.quantity ?? row.quantity,
                fields.rate ?? row.rate
              ),
            }
          : row
      )
    );
  };

  const updateOpeningBalance = (key, qty, rate, changedField = null) => {
    const qtyNum = toNumber(qty);
    const rateNum = toNumber(rate);
    
    // Only show warnings for zero values, don't block the input
    if (changedField === 'quantity' && qty !== '' && qty !== null && qty !== undefined && qtyNum <= 0) {
      showWarningSnackbar('Stock Quantity should be greater than zero for valid entries.');
    }
    if (changedField === 'rate' && rate !== '' && rate !== null && rate !== undefined && rateNum <= 0) {
      showWarningSnackbar('Rate/unit should be greater than zero for valid entries.');
    }
    
    setDataSource((src) =>
      src.map((row) =>
        row.key === key
          ? {
              ...row,
              quantity: qty === '' ? '' : qtyNum,
              rate: rate === '' ? '' : rateNum,
              openingBalance: computeOB(qty, rate),
            }
          : row
      )
    );
  };

  const editDraft = async (key) => {
    // Only allow editing in edit mode when saved items exist
    if (!enableEditMode && hasSavedItems) {
      showWarningSnackbar('Please click "Enable Edit" button to modify existing items');
      return;
    }
    
    // Find the row to edit
    const rowToEdit = dataSource.find((r) => r.key === key);
    if (rowToEdit) {
      // Update input maps with current row values to ensure correct display
      setCodeInputMap((m) => ({ ...m, [key]: rowToEdit.itemCode || '' }));
      setNameInputMap((m) => ({ ...m, [key]: rowToEdit.itemName || '' }));
      
      // Ensure the item is available in itemMaster for autocomplete
      const lookup = rowToEdit.itemCode || rowToEdit.itemName;
      if (lookup) {
        const found = itemMaster.some(
          (im) =>
            (rowToEdit.itemCode && im.itemcode === rowToEdit.itemCode) ||
            (rowToEdit.itemName && im.itemname === rowToEdit.itemName)
        );
        if (!found) {
          // If item not found in current itemMaster, fetch and merge with default rows
          await fetchAndMergeItemData(rowToEdit.itemCode, rowToEdit.itemName);
          
          // Also add the current item to itemMaster if it has valid data (fallback)
          if (rowToEdit.itemCode && rowToEdit.itemName && rowToEdit.itemid) {
            const itemToAdd = {
              itemid: rowToEdit.itemid,
              itemcode: rowToEdit.itemCode,
              itemname: rowToEdit.itemName,
              measurementcd: rowToEdit.units,
              units: rowToEdit.units,
              is_active: true
            };
            setItemMaster(prev => {
              const existingIds = new Set(prev.map(item => item.itemid));
              if (!existingIds.has(itemToAdd.itemid)) {
                return [...prev, itemToAdd];
              }
              return prev;
            });
          }
        }
      }
      
      // Don't overwrite originalRef for already saved rows - we need to preserve the original saved state
      // Only add to originalRef if it doesn't exist (for new rows that become saved)
      const currentOriginalIndex = originalRef.current.findIndex((o) => o.key === key);
      if (currentOriginalIndex < 0) {
        // Only add if not already in originalRef (for new rows)
        originalRef.current.push({ ...rowToEdit });
      }
      // For already saved rows, we keep the existing originalRef entry to preserve the true original state
    }
    
    setDataSource((src) =>
      src.map((r) => (r.key === key ? { ...r, isEditable: true, isDraft: false } : r))
    );
  };

  const saveDraft = (key) => {
    const row = dataSource.find((r) => r.key === key);
    if (!row) return;

    if (row.new) {
      // For new rows: if no values entered, show validation message
      if (!hasAnyValues(row)) {
        showWarningSnackbar('Please fill in Item Code/Name, Stock Quantity, and Rate/unit before saving');
        return;
      }
      // If values entered but incomplete, show warning
      if (!isCompleteRow(row)) {
        const missingFields = [];
        if (!row.itemCode && !row.itemName) missingFields.push('Item Code or Item Name');
        if (!row.quantity || row.quantity <= 0) missingFields.push('Stock Quantity');
        if (!row.rate || row.rate <= 0) missingFields.push('Rate/unit');
        
        showWarningSnackbar(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        return;
      }
      // Check for zero quantity or rate - be more strict
      const qtyNum = toNumber(row.quantity);
      const rateNum = toNumber(row.rate);
      
      if (qtyNum <= 0 || row.quantity === '' || row.quantity === null || row.quantity === undefined) {
        showErrorSnackbar('Stock Quantity must be greater than zero. Please enter a valid quantity.');
        return;
      }
      if (rateNum <= 0 || row.rate === '' || row.rate === null || row.rate === undefined) {
        showErrorSnackbar('Rate/unit must be greater than zero. Please enter a valid rate per unit.');
        return;
      }
    } else {
      // Existing rows: Always validate required fields before saving, even if nothing changed
      if (!isRowValidForUpdate(row)) {
        const missingFields = [];
        if (!row.itemCode && !row.itemName) missingFields.push('Item Code or Item Name');
        if (!row.quantity || row.quantity <= 0) missingFields.push('Stock Quantity');
        if (!row.rate || row.rate <= 0) missingFields.push('Rate/unit');
        
        showErrorSnackbar(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        return;
      }
      
      // If nothing changed, just exit edit mode
      if (!isRowChanged(row)) {
        setDataSource((src) =>
          src.map((r) => (r.key === key ? { ...r, isEditable: false } : r))
        );
        return;
      }
    }

    // Prevent duplicate (same itemCode OR itemName & rate)
    const duplicateItem = dataSource.find(
      (it) => it.key !== key && 
      ((row.itemCode && it.itemCode === row.itemCode) || (row.itemName && it.itemName === row.itemName)) && 
      eq2(it.rate, row.rate)
    );
    if (duplicateItem) {
      const duplicateIdentifier = row.itemCode || row.itemName;
      showErrorSnackbar(
        `Item "${duplicateIdentifier}" already exists with the same rate. Please edit the existing entry instead of creating a duplicate.`
      );
      return;
    }

    setDataSource((src) => {
      const updatedSrc = src.map((r) =>
        r.key === key
          ? { ...r, isEditable: false, isDraft: true, new: false }
          : r
      );
      
      // Add new empty row when in edit mode OR when no actually saved items exist
      const hasActuallySavedItems = updatedSrc.some(row => !row.new && !row.isDraft);
      const hasEmptyEditableRow = updatedSrc.some(row => isEmptyRow(row) && row.isEditable);
      
      if ((enableEditMode || !hasActuallySavedItems) && !hasEmptyEditableRow) {
        const newRow = {
          key: `new-${Date.now()}`,
          slNo: updatedSrc.length + 1,
          itemCode: '',
          itemName: '',
          units: '',
          quantity: null,
          rate: null,
          openingBalance: 0,
          isEditable: true,
          isDraft: false,
          new: true,
          itemid: '',
        };
        updatedSrc.push(newRow);
      }
      
      return recalculateSerialNumbers(updatedSrc);
    });
    setAutoRowAdded(true);
  };

  const addNewRow = (check = true, makeEditable = true) => {
    // Only show warning if manually adding and not in edit mode
    const hasExistingRows = dataSource.some(row => !row.new);
    if (!enableEditMode && hasExistingRows && check) {
      showWarningSnackbar('Please click "Enable Edit" button to add new items to your opening stock');
      return;
    }
    
    if (check && dataSource.some((r) => r.isEditable && hasAnyValues(r))) {
      showWarningSnackbar(
        'Please complete and save the current row (Item Code/Name, Stock Quantity, and Rate/unit) before adding a new item'
      );
      return;
    }
    
    if (makeEditable && check) {
      const hasEmptyEditableRow = dataSource.some(row => isEmptyRow(row) && row.isEditable);
      if (hasEmptyEditableRow) {
        showWarningSnackbar('There is already an empty row available for editing. Please use that row first.');
        return;
      }
    }
    
    setDataSource((src) => {
      const newRow = {
        key: `new-${Date.now()}`, // Use timestamp for unique keys
        slNo: src.length + 1, 
        itemCode: '',
        itemName: '',
        units: '',
        quantity: null,
        rate: null,
        openingBalance: 0,
        isEditable: makeEditable,
        isDraft: false,
        new: true,
        itemid: '',
      };
      const updatedData = [...src, newRow];
      return recalculateSerialNumbers(updatedData);
    });
    setAutoRowAdded(true);
  };

  const removeRow = async (key, forceDelete = false) => {
    const row = dataSource.find((r) => r.key === key);
    if (!row) return;

    if (!row.new && !forceDelete) {
      // Find the original data from originalRef using the same key
      const originalData = originalRef.current.find((o) => o.key === key);
      if (originalData) {
        // Update input maps to match the restored original data
        setCodeInputMap((m) => ({ ...m, [key]: originalData.itemCode || '' }));
        setNameInputMap((m) => ({ ...m, [key]: originalData.itemName || '' }));
        
        // Ensure the restored item is available in itemMaster for autocomplete
        const lookup = originalData.itemCode || originalData.itemName;
        if (lookup) {
          const found = itemMaster.some(
            (im) =>
              (originalData.itemCode && im.itemcode === originalData.itemCode) ||
              (originalData.itemName && im.itemname === originalData.itemName)
          );
          if (!found) {
            // If item not found in current itemMaster, fetch and merge with default rows
            await fetchAndMergeItemData(originalData.itemCode, originalData.itemName);
            
            // Also add the restored item to itemMaster if it has valid data (fallback)
            if (originalData.itemCode && originalData.itemName && originalData.itemid) {
              const itemToAdd = {
                itemid: originalData.itemid,
                itemcode: originalData.itemCode,
                itemname: originalData.itemName,
                measurementcd: originalData.units,
                units: originalData.units,
                is_active: true
              };
              setItemMaster(prev => {
                const existingIds = new Set(prev.map(item => item.itemid));
                if (!existingIds.has(itemToAdd.itemid)) {
                  return [...prev, itemToAdd];
                }
                return prev;
              });
            }
          }
        }
        
        setDataSource((src) =>
          src.map((r) =>
            r.key === key
              ? {
                  ...originalData,
                  isEditable: false,
                  isDraft: false,
                }
              : r
          )
        );
      } else {
        // If no original data found, just exit edit mode and clear input maps
        setCodeInputMap((m) => ({ ...m, [key]: row.itemCode || '' }));
        setNameInputMap((m) => ({ ...m, [key]: row.itemName || '' }));
        
        setDataSource((src) =>
          src.map((r) => (r.key === key ? { ...r, isEditable: false } : r))
        );
      }
      return;
    }

    // For new rows or force delete, remove the row and clean up input maps
    setCodeInputMap((m) => {
      const newMap = { ...m };
      delete newMap[key];
      return newMap;
    });
    setNameInputMap((m) => {
      const newMap = { ...m };
      delete newMap[key];
      return newMap;
    });

    const filtered = dataSource.filter((r) => r.key !== key);
    if (filtered.length === 0) {
    }
    
    if (forceDelete && !row.new) {
      setPendingDeletions(prev => [...prev, key]);
    }
    
    setDataSource(filtered.map((r, i) => ({ ...r, slNo: i + 1 })));
  };

  const getTotal = (check = true) => {
    const total = round2(
      dataSource.reduce((sum, r) => sum + computeOB(r.quantity, r.rate), 0)
    );

    return total;
  };

  const handleSave = async () => {
    const effectiveRows = dataSource.filter((r) => hasAnyValues(r));

    // First validate ALL existing rows to ensure they have required fields
    const invalidExistingRows = effectiveRows.filter((r) => {
      if (r.new) return false; // Skip new rows for this validation
      
      const qtyNum = toNumber(r.quantity);
      const rateNum = toNumber(r.rate);
      const hasValidItem = r.itemCode || r.itemName;
      const hasValidQuantity = qtyNum > 0 && r.quantity !== '' && r.quantity !== null && r.quantity !== undefined;
      const hasValidRate = rateNum > 0 && r.rate !== '' && r.rate !== null && r.rate !== undefined;
      
      return !hasValidItem || !hasValidQuantity || !hasValidRate;
    });

    if (invalidExistingRows.length > 0) {
      const missingFields = [];
      invalidExistingRows.forEach(r => {
        if (!r.itemCode && !r.itemName) missingFields.push('Item Code or Item Name');
        if (!r.quantity || r.quantity <= 0) missingFields.push('Stock Quantity');
        if (!r.rate || r.rate <= 0) missingFields.push('Rate/unit');
      });
      
      const uniqueMissingFields = [...new Set(missingFields)];
      showErrorSnackbar(`Please ensure all existing items have valid ${uniqueMissingFields.join(', ')} values before saving`);
      return;
    }

    const rowsToPersist = effectiveRows.filter(
      (r) => (r.new && isCompleteRow(r)) || (!r.new && isRowChanged(r))
    );

    // Validate required fields and zero values for rows that will be persisted
    const invalidRows = rowsToPersist.filter((r) => {
      const qtyNum = toNumber(r.quantity);
      const rateNum = toNumber(r.rate);
      const hasValidItem = r.itemCode || r.itemName;
      const hasValidQuantity = qtyNum > 0 && r.quantity !== '' && r.quantity !== null && r.quantity !== undefined;
      const hasValidRate = rateNum > 0 && r.rate !== '' && r.rate !== null && r.rate !== undefined;
      
      return !hasValidItem || !hasValidQuantity || !hasValidRate;
    });
    
    if (invalidRows.length > 0) {
      const missingFields = [];
      invalidRows.forEach(r => {
        if (!r.itemCode && !r.itemName) missingFields.push('Item Code or Item Name');
        if (!r.quantity || r.quantity <= 0) missingFields.push('Stock Quantity');
        if (!r.rate || r.rate <= 0) missingFields.push('Rate/unit');
      });
      
      const uniqueMissingFields = [...new Set(missingFields)];
      showErrorSnackbar(`Please ensure all items have valid ${uniqueMissingFields.join(', ')} values before saving`);
      return;
    }

    const initialCount = originalRef.current.length;
    const currentExistingCount = effectiveRows.filter((r) => !r.new).length;
    const hasRemoval = currentExistingCount < initialCount;

    const hasUnsavedEdit = dataSource.some((r) => r.isEditable && hasAnyValues(r));
    if (hasUnsavedEdit) {
      showWarningSnackbar(
        'Please complete and save the row you are currently editing (click the ✓ button) before saving all changes.'
      );
      return;
    }

    if (rowsToPersist.length === 0 && !hasRemoval) {
      showWarningSnackbar('No changes detected. Please make some changes before saving.');
      return;
    }

    const items = effectiveRows.map((r) => ({
      itemid: r.itemid || '',
      purchaseprice: round2(r.rate || 0),
      quantity: round2(r.quantity || 0),
    }));

    setSaveLoading(true);
    try {
      const resp = await createItemOpeningStock({
        createItemOpeningStockInput: {
          items,
          openingstockdate: entryDate,
        },
      });

      if (resp?.data) {
        const successMessage = enableEditMode ? 'Opening stock updated successfully' : 'Opening stock saved successfully';
        showSuccessSnackbar(successMessage);

        const merged = effectiveRows.map((r) => ({
          ...r,
          isDraft: false,
          new: false,
          openingBalance: computeOB(r.quantity, r.rate),
        }));
        const recalculatedMerged = recalculateSerialNumbers(merged);
        originalRef.current = recalculatedMerged.map((m, idx) => ({
          ...m,
          key: originalRef.current[idx]?.key ?? m.key,
        }));
        setDataSource(recalculatedMerged);
        
        setPendingDeletions([]);
        setEnableEditMode(false);
        setAutoRowAdded(false);
        // Clear input maps after successful save
        setCodeInputMap({});
        setNameInputMap({});

        const tot = getTotal(false);
        if (data?.accountid === 24) {
          updateLedger(data?.accountid, 'debit', tot, true);
          if (onSaveOpeningBalance && accountOpeningBalance) {
            try {
              // Find the account by accountid and update its debit balance
              const updatedAccountData = accountOpeningBalance.map(account => {
                if (account.accountid === data.accountid) {
                  return {
                    ...account,
                    debitamount: tot
                  };
                }
                return account;
              });
              
              await onSaveOpeningBalance(updatedAccountData, entryDate, 'save');
              dispatch(listAccountOpeningBalanceApi.util.invalidateTags(['listAccountOpeningBalance']));
              dispatch(dashboardApi.util.invalidateTags(['dashboard',"bizview"]));
              dispatch(dashboardApi.util.invalidateTags(['Dashboard_closingStock']));
            } catch (error) {
            }
          }
          onClose();
          return;
        }

      }
    } catch (e) {
      showErrorSnackbar('Error saving opening stock');
    } finally {
      dispatch(setModalBlocked(false));
      setSaveLoading(false);
    }
  };

  // const handleOpeningBalance = () => {
  //   const total = getTotal(false);
  //   updateLedger(data?.accountid, 'debit', total, true);
  //   setConfirmModal(initialConfirmModalState);
  //   dispatch(setNavigationBlocked(true));
  //   onClose();
  // };

  const MAX_QTY = 9999999999;
  const MAX_RATE = 9999999999;
  const columns = [
    { field: 'slNo', headerName: 'Sl.No', align: 'center', minWidth: 30 },
    {
      field: 'itemCode',
      headerName: 'Item Code',
      minWidth: 120,
      renderCell: ({ row }) => {
        if (!row.isEditable) return <Box>{row.itemCode}</Box>;

        const inputValue =
          codeInputMap[row.key] !== undefined ? codeInputMap[row.key] : row.itemCode;
        const selectedOption = itemMaster.find((i) => i.itemcode === inputValue);

        const rowIndex = dataSource.findIndex(r => r.key === row.key);
        
        return (
          <Autocomplete
            freeSolo
            fullWidth
            options={[{ isAddItem: true, itemcode: 'ADD_ITEM_PLACEHOLDER', itemid: 'add-item' }, ...itemMaster]}
            getOptionLabel={(opt) => {
              if (opt.isAddItem) return "Add Item";
              return typeof opt === 'string' ? opt : opt.itemcode;
            }}
            getOptionKey={(option) => {
              if (option.isAddItem) return 'add-item';
              return `item-${option.itemid}`;
            }}
            filterOptions={(options, { inputValue }) => {
              // Always create Add Item option explicitly
              const addItemOption = { isAddItem: true, itemcode: 'ADD_ITEM_PLACEHOLDER', itemid: 'add-item' };
              
              if (inputValue.trim() === '') {
                // No filter - show Add Item at top, then all items
                const regularItems = options.filter(opt => !opt.isAddItem);
                return [addItemOption, ...regularItems];
              }
              
              // Apply filtering to non-addItem options
              const filteredItems = options.filter(option => 
                !option.isAddItem && 
                (option.itemcode.toLowerCase().includes(inputValue.toLowerCase()) ||
                 option.itemname.toLowerCase().includes(inputValue.toLowerCase()))
              );
              
              // Always put "Add Item" at the TOP, then show filtered results
              return [addItemOption, ...filteredItems];
            }}
            renderOption={(props, option) => (
              option.isAddItem ? (
                <MenuItem
                  value="addItem"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleAddItemClick(rowIndex, 'itemCode');
                  }}
                  sx={{
                    backgroundColor: 'white',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    borderBottom: '1px solid #e0e0e0',
                    marginBottom: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <AddBox
                    fontSize="small"
                    sx={{ marginRight: '5px', color: 'primary.main' }}
                  />
                  <ListItemText sx={{ color: 'primary.main' }} primary="Add Item" />
                </MenuItem>
              ) : (
                <MenuItem 
                  {...props} 
                  key={option.itemid} 
                  value={option.itemcode}
                  sx={{
                    marginTop: option.itemcode === itemMaster[0]?.itemcode ? '4px' : '0px',
                  }}
                >
                  <ListItemText primary={option.itemcode} />
                </MenuItem>
              )
            )}
            loading={false}
            noOptionsText={inputValue && inputValue.trim() !== '' ? "No matching items found" : "No items found"}
            ListboxProps={ListboxProps}
            PaperComponent={PaperComponent}
            inputValue={inputValue}
            onInputChange={(_, newVal, reason) => {
              if (reason === 'input') {
                setCodeInputMap((m) => ({ ...m, [row.key]: newVal }));
                setItemMasterFrom(0);
                fetchItemMaster(newVal || '', 0, 20);
              }
            }}
            onOpen={async () => {
              // When dropdown opens, ensure the current item is available in itemMaster
              if (inputValue && inputValue.trim() !== '') {
                const foundInItemMaster = itemMaster.find(i => i.itemcode === inputValue);
                if (!foundInItemMaster) {
                  // Item not found in itemMaster - add it if it's a valid row item
                  if (inputValue === row.itemCode && row.itemCode && row.itemName && row.itemid) {
                    const itemToAdd = {
                      itemid: row.itemid,
                      itemcode: row.itemCode,
                      itemname: row.itemName,
                      measurementcd: row.units,
                      units: row.units,
                      is_active: true
                    };
                    setItemMaster(prev => {
                      const existingIds = new Set(prev.map(item => item.itemid));
                      if (!existingIds.has(itemToAdd.itemid)) {
                        return [...prev, itemToAdd];
                      }
                      return prev;
                    });
                  } else {
                    // Invalid input - fetch and merge with default rows
                    await fetchAndMergeItemData(inputValue, '');
                    if (!itemMaster.find(i => i.itemcode === inputValue)) {
                      // Still not found after fetch - clear everything
                      setCodeInputMap((m) => ({ ...m, [row.key]: '' }));
                      setNameInputMap((m) => ({ ...m, [row.key]: '' }));
                    }
                  }
                }
              } else {
                // Empty field - fetch and merge with default rows
                await fetchAndMergeItemData('', '');
              }
            }}
            value={selectedOption || inputValue}
            onBlur={() => {
              // When user moves away from field, only clear if input is invalid
              // Use the same logic as inputValue calculation
              const currentInput = codeInputMap[row.key] !== undefined ? codeInputMap[row.key] : row.itemCode;
              const rowItemCode = row.itemCode;
              
              // Check if current input is valid
              const isValidInput = itemMaster.find(i => i.itemcode === currentInput);
              // Check if row has valid item code
              const isValidRowCode = itemMaster.find(i => i.itemcode === rowItemCode);
              
              // If user just focused out without typing anything (currentInput equals row.itemCode)
              // and the row has valid data, preserve the values
              if (currentInput === rowItemCode && isValidRowCode) {
                // User just focused out without changes - preserve the values
                setCodeInputMap((m) => ({ ...m, [row.key]: rowItemCode }));
                setNameInputMap((m) => ({ ...m, [row.key]: row.itemName }));
                return;
              }
              
              // Only clear if:
              // 1. User typed something invalid (currentInput exists but not valid)
              // 2. AND the row doesn't have a valid item code to fall back to
              if (currentInput && !isValidInput && currentInput.trim() !== '' && !isValidRowCode) {
                // Clear all fields for invalid input
                setCodeInputMap((m) => ({ ...m, [row.key]: '' }));
                setNameInputMap((m) => ({ ...m, [row.key]: '' }));
                handleFieldChange(row.key, {
                  itemCode: '',
                  itemName: '',
                  units: '',
                  itemid: '',
                });
              } else if (currentInput && !isValidInput && currentInput.trim() !== '' && isValidRowCode) {
                // User typed invalid input but row has valid code - restore row values
                setCodeInputMap((m) => ({ ...m, [row.key]: rowItemCode }));
                setNameInputMap((m) => ({ ...m, [row.key]: row.itemName }));
              }
            }}
            onChange={(_, val) => {
              if (val && val.isAddItem) {
                handleAddItemClick(rowIndex, 'itemCode');
                return;
              }
              
              if (typeof val === 'string') {
                setCodeInputMap((m) => ({ ...m, [row.key]: val }));
                setNameInputMap((m) => ({ ...m, [row.key]: '' })); // Clear name when typing code
                handleFieldChange(row.key, {
                  itemCode: val,
                  itemName: '',  // Clear item name when typing invalid code
                  units: '',     // Clear units when typing invalid code
                  itemid: '',    // Clear itemid when typing invalid code
                });
              } else if (val && val.itemcode) {
                setCodeInputMap((m) => ({ ...m, [row.key]: val.itemcode }));
                setNameInputMap((m) => ({ ...m, [row.key]: val.itemname })); // Set name map too
                handleFieldChange(row.key, {
                  itemCode: val.itemcode,
                  itemName: val.itemname,
                  units: val.measurementcd,
                  itemid: val.itemid,
                });
              } else {
                setCodeInputMap((m) => ({ ...m, [row.key]: '' }));
                setNameInputMap((m) => ({ ...m, [row.key]: '' }));
                handleFieldChange(row.key, {
                  itemCode: '',
                  itemName: '',
                  units: '',
                  itemid: '',
                });
              }
            }}
            renderOption={(props, option) => (
              option.isAddItem ? (
                <MenuItem
                  value="addItem"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleAddItemClick(rowIndex, 'itemCode');
                  }}
                  sx={{
                    backgroundColor: 'white',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    borderBottom: '1px solid #e0e0e0',
                    marginBottom: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <AddBox
                    fontSize="small"
                    sx={{ marginRight: '5px', color: 'primary.main' }}
                  />
                  <ListItemText sx={{ color: 'primary.main' }} primary="Add Item" />
                </MenuItem>
              ) : (
                <MenuItem 
                  {...props} 
                  key={option.itemid} 
                  value={option.itemcode}
                  sx={{
                    marginTop: option.itemcode === itemMaster[0]?.itemcode ? '4px' : '0px',
                  }}
                >
                  <ListItemText primary={option.itemcode} />
                </MenuItem>
              )
            )}
            renderInput={(params) => <TextField {...params} variant="standard" />}
          />
        );
      },
    },
    {
      field: 'itemName',
      headerName: 'Item Name',
      align: 'left',
      minWidth: 200,
      renderCell: ({ row }) => {
        if (!row.isEditable) return <Box>{row.itemName}</Box>;

        const inputValue =
          nameInputMap[row.key] !== undefined ? nameInputMap[row.key] : row.itemName;
        const selectedOption = itemMaster.find((i) => i.itemname === inputValue);

        const rowIndex = dataSource.findIndex(r => r.key === row.key);
        
        return (
          <Autocomplete
            freeSolo
            fullWidth
            options={[{ isAddItem: true, itemcode: 'ADD_ITEM_PLACEHOLDER', itemid: 'add-item' }, ...itemMaster]}
            getOptionLabel={(opt) => {
              if (opt.isAddItem) return "Add Item";
              return typeof opt === 'string' ? opt : opt.itemname;
            }}
            getOptionKey={(option) => {
              if (option.isAddItem) return 'add-item';
              return `item-${option.itemid}`;
            }}
            filterOptions={(options, { inputValue }) => {
              // Always create Add Item option explicitly
              const addItemOption = { isAddItem: true, itemcode: 'ADD_ITEM_PLACEHOLDER', itemid: 'add-item' };
              
              if (inputValue.trim() === '') {
                // No filter - show Add Item at top, then all items
                const regularItems = options.filter(opt => !opt.isAddItem);
                return [addItemOption, ...regularItems];
              }
              
              // Apply filtering to non-addItem options
              const filteredItems = options.filter(option => 
                !option.isAddItem && 
                (option.itemcode.toLowerCase().includes(inputValue.toLowerCase()) ||
                 option.itemname.toLowerCase().includes(inputValue.toLowerCase()))
              );
              
              // Always put "Add Item" at the TOP, then show filtered results
              return [addItemOption, ...filteredItems];
            }}
            loading={false}
            noOptionsText={inputValue && inputValue.trim() !== '' ? "No matching items found" : "No items found"}
            ListboxProps={ListboxProps}
            PaperComponent={PaperComponent}
            inputValue={inputValue}
            onInputChange={(_, newVal, reason) => {
              if (reason === 'input') {
                setNameInputMap((m) => ({ ...m, [row.key]: newVal }));
                setItemMasterFrom(0);
                fetchItemMaster(newVal || '', 0, 20);
              }
            }}

            onOpen={async () => {
              // When dropdown opens, ensure the current item is available in itemMaster
              if (inputValue && inputValue.trim() !== '') {
                const foundInItemMaster = itemMaster.find(i => i.itemname === inputValue);
                if (!foundInItemMaster) {
                  // Item not found in itemMaster - add it if it's a valid row item
                  if (inputValue === row.itemName && row.itemCode && row.itemName && row.itemid) {
                    const itemToAdd = {
                      itemid: row.itemid,
                      itemcode: row.itemCode,
                      itemname: row.itemName,
                      measurementcd: row.units,
                      units: row.units,
                      is_active: true
                    };
                    setItemMaster(prev => {
                      const existingIds = new Set(prev.map(item => item.itemid));
                      if (!existingIds.has(itemToAdd.itemid)) {
                        return [...prev, itemToAdd];
                      }
                      return prev;
                    });
                  } else {
                    // Invalid input - fetch and merge with default rows
                    await fetchAndMergeItemData('', inputValue);
                    if (!itemMaster.find(i => i.itemname === inputValue)) {
                      // Still not found after fetch - clear everything
                      setNameInputMap((m) => ({ ...m, [row.key]: '' }));
                      setCodeInputMap((m) => ({ ...m, [row.key]: '' }));
                    }
                  }
                }
              } else {
                // Empty field - fetch and merge with default rows
                await fetchAndMergeItemData('', '');
              }
            }}
            value={selectedOption || inputValue}
            onBlur={() => {
              // When user moves away from field, only clear if input is invalid
              // Use the same logic as inputValue calculation
              const currentInput = nameInputMap[row.key] !== undefined ? nameInputMap[row.key] : row.itemName;
              const rowItemName = row.itemName;
              
              // Check if current input is valid
              const isValidInput = itemMaster.find(i => i.itemname === currentInput);
              // Check if row has valid item name
              const isValidRowName = itemMaster.find(i => i.itemname === rowItemName);
              
              // If user just focused out without typing anything (currentInput equals row.itemName)
              // and the row has valid data, preserve the values
              if (currentInput === rowItemName && isValidRowName) {
                // User just focused out without changes - preserve the values
                setNameInputMap((m) => ({ ...m, [row.key]: rowItemName }));
                setCodeInputMap((m) => ({ ...m, [row.key]: row.itemCode }));
                return;
              }
              
              // Only clear if:
              // 1. User typed something invalid (currentInput exists but not valid)
              // 2. AND the row doesn't have a valid item name to fall back to
              if (currentInput && !isValidInput && currentInput.trim() !== '' && !isValidRowName) {
                // Clear all fields for invalid input
                setNameInputMap((m) => ({ ...m, [row.key]: '' }));
                setCodeInputMap((m) => ({ ...m, [row.key]: '' }));
                handleFieldChange(row.key, {
                  itemCode: '',
                  itemName: '',
                  units: '',
                  itemid: '',
                });
              } else if (currentInput && !isValidInput && currentInput.trim() !== '' && isValidRowName) {
                // User typed invalid input but row has valid name - restore row values
                setNameInputMap((m) => ({ ...m, [row.key]: rowItemName }));
                setCodeInputMap((m) => ({ ...m, [row.key]: row.itemCode }));
              }
            }}
            onChange={(_, val) => {
              if (val && val.isAddItem) {
                handleAddItemClick(rowIndex, 'itemName');
                return;
              }
              
              if (typeof val === 'string') {
                setNameInputMap((m) => ({ ...m, [row.key]: val }));
                setCodeInputMap((m) => ({ ...m, [row.key]: '' })); // Clear code when typing name
                handleFieldChange(row.key, {
                  itemCode: '',  // Clear item code when typing invalid name
                  itemName: val,
                  units: '',     // Clear units when typing invalid name
                  itemid: '',    // Clear itemid when typing invalid name
                });
              } else if (val && val.itemname) {
                setNameInputMap((m) => ({ ...m, [row.key]: val.itemname }));
                setCodeInputMap((m) => ({ ...m, [row.key]: val.itemcode })); // Set code map too
                handleFieldChange(row.key, {
                  itemCode: val.itemcode,
                  itemName: val.itemname,
                  units: val.measurementcd,
                  itemid: val.itemid,
                });
              } else {
                setNameInputMap((m) => ({ ...m, [row.key]: '' }));
                setCodeInputMap((m) => ({ ...m, [row.key]: '' }));
                handleFieldChange(row.key, {
                  itemCode: '',
                  itemName: '',
                  units: '',
                  itemid: '',
                });
              }
            }}
            renderOption={(props, option) => (
              option.isAddItem ? (
                <MenuItem
                  value="addItem"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleAddItemClick(rowIndex, 'itemName');
                  }}
                  sx={{
                    backgroundColor: 'white',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    borderBottom: '1px solid #e0e0e0',
                    marginBottom: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <AddBox
                    fontSize="small"
                    sx={{ marginRight: '5px', color: 'primary.main' }}
                  />
                  <ListItemText sx={{ color: 'primary.main' }} primary="Add Item" />
                </MenuItem>
              ) : (
                <MenuItem {...props} key={option.itemid} value={option.itemname}>
                  <ListItemText primary={option.itemname} />
                </MenuItem>
              )
            )}
            renderInput={(params) => <TextField {...params} variant="standard" />}
          />
        );
      },
    },
    {
      field: 'units',
      headerName: 'Units',
      align: 'left',
      minWidth: 50,
      renderCell: (params) => <Box>{params.row.units}</Box>,
    },
    {
      field: 'quantity',
      headerName: 'Stock Quantity',
      align: 'right',
      minWidth: 20,
      renderCell: (params) => {
        const isItemSelected = params.row.itemCode || params.row.itemName;
        const isFieldEnabled = params.row.isEditable && isItemSelected;
        
        return isFieldEnabled ? (
          <TextField
            type="number"
            variant="standard"
            value={params.row.quantity ?? ''}
            onChange={(e) => {
              const rawValue = e.target.value.trim();
              const numValue = Number(rawValue);
              if (!isNaN(numValue) && numValue > MAX_QTY) return;
              updateOpeningBalance(params.row.key, rawValue, params.row.rate, 'quantity');
            }}
            inputProps={{
              style: { textAlign: 'right' },
              min: 1,
              max: MAX_QTY,
              step: 'any',
            }}
            onFocus={(e) => (e.target).select()}
            onKeyDown={(e) => {
              if (e.key === 'e' || e.key === 'E' || e.key === '-') {
                e.preventDefault();
              }
            }}
          />
        ) : (
          <Box sx={{ textAlign: 'right' }}>{MoneyFormatv3(params.row.quantity || 0)}</Box>
        );
      },
    },
    {
      field: 'rate',
      headerName: 'Rate/unit',
      align: 'right',
      minWidth: 120,
      renderCell: (params) => {
        const isItemSelected = params.row.itemCode || params.row.itemName;
        const isFieldEnabled = params.row.isEditable && isItemSelected;
        
        return isFieldEnabled ? (
          <TextField
            type="text"
            variant="standard"
            value={params.row.rate ?? ''}
            onChange={(e) => {
              const rawValue = e.target.value.trim();
              const numValue = Number(rawValue);
              if (!isNaN(numValue) && numValue > MAX_RATE) return;
              updateOpeningBalance(params.row.key, params.row.quantity, rawValue, 'rate');
            }}
            inputProps={{
              style: { textAlign: 'right' },
              min: 0,
              max: MAX_RATE,
              step: 'any',
            }}
            onFocus={(e) => (e.target).select()}
            onKeyDown={(e) => {
              if (e.key === 'e' || e.key === 'E' || e.key === '-') {
                e.preventDefault();
              }
            }}
          />
        ) : (
          <Box sx={{ textAlign: 'right' }}>₹ {MoneyFormatv3(params.row.rate || 0)}</Box>
        );
      },
    },
    {
      field: 'openingBalance',
      headerName: 'Opening Balance',
      align: 'right',
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ textAlign: 'right' }}>₹ {MoneyFormatv3(params.row.openingBalance || 0)}</Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      align: 'center',
      minWidth: 80,
      renderCell: (params) => (
        <Box className="actionBackGround">
          <Box className="actionCellStyle">
            {params.row.isEditable && params.row.new && (
              <Tooltip title="Save" placement="left" disableInteractive arrow>
                <IconButton
                  onClick={() => saveDraft(params.row.key)}
                  sx={{
                    margin: '0px 2px',
                    padding: '5px',
                    backgroundColor: 'white',
                    border: 'solid 1px black',
                  }}
                >
                  <CheckIcon sx={{ fontSize: '10px', color: 'green' }} />
                </IconButton>
              </Tooltip>
            )}

            {params.row.isEditable && !params.row.new && (
              <Tooltip title="Update" placement="left" disableInteractive arrow>
                <IconButton
                  onClick={() => saveDraft(params.row.key)}
                  sx={{
                    margin: '0px 2px',
                    padding: '5px',
                    backgroundColor: 'white',
                    border: 'solid 1px black',
                  }}
                >
                  <CheckIcon sx={{ fontSize: '10px', color: 'green' }} />
                </IconButton>
              </Tooltip>
            )}

            {params.row.isEditable && (
              <Tooltip title="Cancel" placement="top" disableInteractive arrow>
                <IconButton
                  onClick={() => removeRow(params.row.key)}
                  sx={{
                    margin: '0px 2px',
                    padding: '5px',
                    backgroundColor: 'white',
                    border: 'solid 1px black',
                  }}
                >
                  <CloseIcon sx={{ fontSize: '10px', color: '#eb6a6a' }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Edit and Delete buttons show for non-editable, non-new items when no saved items OR in edit mode */}
            {!params.row.isEditable && !params.row.new && (!hasSavedItems || enableEditMode) && (
              <>
                <Tooltip title="Edit" placement="right" disableInteractive arrow>
                  <IconButton
                    onClick={() => editDraft(params.row.key)}
                    sx={{
                      margin: '0px 2px',
                      padding: '5px',
                      backgroundColor: '#D0EFFF',
                      border: 'solid 1px black',
                    }}
                  >
                    <EditIcon sx={{ fontSize: '10px', color: '#6096d9' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete" placement="top" disableInteractive arrow>
                  <IconButton
                    onClick={() => removeRow(params.row.key, true)}
                    sx={{
                      margin: '0px 2px',
                      padding: '5px',
                      backgroundColor: 'white',
                      border: 'solid 1px black',
                    }}
                  >
                    <Delete sx={{ fontSize: '10px', color: '#eb6a6a' }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
      ),
    },
  ];

  if (stockSummaryLoading || unitListLoading || loading) {
    return (
      <Dialog open={data.isModalOpen} maxWidth="xxl" fullWidth>
        <DialogTitle>
          <Skeleton variant="text" width="40%" height={32} />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Skeleton variant="rectangular" width={120} height={36} />
            <Skeleton variant="rectangular" width="100%" height={48} />
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" width="100%" height={40} />
            ))}
            <Skeleton variant="rectangular" width={160} height={36} />
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
  };

  const hasSavedItems = dataSource.some(row => !row.new && !row.isDraft);
  const filteredDataSource = enableEditMode || !hasSavedItems ? dataSource : dataSource.filter(row => !isEmptyRow(row));
  
  const visibleRows = filteredDataSource.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Dialog open={data.isModalOpen} maxWidth="xxl" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0,
          p: { xs: 1, sm: 1.5 },
          minHeight: 'auto',
        }}
      >
        <div>Item – Opening Stock and Balances</div>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: 'calc(100vh - 150px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <TableContainer component={Paper} sx={{ width: '100%', flex: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.field}
                      align={col.align}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        whiteSpace: 'nowrap',
                        minWidth: col.minWidth,
                        py: 0.5,
                      }}
                    >
                      {col.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow className='noTableCellBorder' key={row.key} hover>
                    {columns.map((col) => (
                      <TableCell
                        key={`${row.key}-${col.field}`}
                        align={col.field === 'actions' ? 'center' : col.align}
                        sx={{
                          minWidth: col.minWidth,
                          whiteSpace: 'nowrap',
                          fontSize: 14,
                          fontWeight: 'bold',
                          py: 0.5,
                        }}
                      >
                        {col.renderCell ? col.renderCell({ row }) : (row)[col.field]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* Add New Line button - only when no saved items OR in edit mode with saved items */}
                {(!hasSavedItems || (hasSavedItems && enableEditMode)) && (
                  <TableRow>
                    <TableCell
                      sx={{ whiteSpace: 'nowrap', fontSize: 15, fontWeight: 'bold' }}
                      colSpan={8}
                    >
                      <Button
                        startIcon={<AddIcon />}
                        onClick={addNewRow}
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{ 
                          boxShadow: 2, 
                          '&:hover': { transform: 'scale(1.02)', boxShadow: 3 },
                          fontSize: '12px',
                          py: 0.5,
                          px: 1
                        }}
                      >
                        Add New Line
                      </Button>
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell
                    sx={{ whiteSpace: 'nowrap', fontSize: 15, fontWeight: 'bold' }}
                    colSpan={6}
                  >
                    Total
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      fontSize: 15,
                      fontWeight: 'bold',
                      textAlign: 'right',
                    }}
                    colSpan={1}
                  >
                    ₹ {MoneyFormatv3(getTotal())}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredDataSource.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[10]}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />

        </Box>
      </DialogContent>

      <DialogActions sx={{ pr: 3, pb: 2 }}>
        <Button variant="outlined" onClick={handleClose} disabled={saveLoading}>
          Cancel
        </Button>
        {/* No items exist - Show Save button */}
        {!hasSavedItems && (
          <Button
            variant="contained"
            onClick={handleSave}
            color="primary"
            disabled={saveLoading}
            startIcon={saveLoading && <CircularProgress size={20} color="inherit" />}
            sx={{ boxShadow: 2, '&:hover': { transform: 'scale(1.02)', boxShadow: 3 } }}
          >
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        )}
        {/* Items exist - View mode - Show Enable Edit */}
        {hasSavedItems && !enableEditMode && (
          <Button
            variant="contained"
            onClick={handleEnableEdit}
            color="primary"
            disabled={saveLoading}
            startIcon={<EditIcon />}
            sx={{ boxShadow: 2, '&:hover': { transform: 'scale(1.02)', boxShadow: 3 } }}
          >
            Enable Edit
          </Button>
        )}
        {/* Items exist - Edit mode - Show Update */}
        {hasSavedItems && enableEditMode && (
          <Button
            variant="contained"
            onClick={handleSave}
            color="primary"
            disabled={saveLoading}
            startIcon={saveLoading && <CircularProgress size={20} color="inherit" />}
            sx={{ boxShadow: 2, '&:hover': { transform: 'scale(1.02)', boxShadow: 3 } }}
          >
            {saveLoading ? 'Updating...' : 'Update'}
          </Button>
        )}

      </DialogActions>

      {/* Add New Item Modal */}
      <AddNewItemModal
        open={addItemModalOpen}
        setOpen={setAddItemModalOpen}
        modalEvent={true}
        page="openingStock"
        getItems={handleItemAdded}
        invDate={entryDate}
        rowIndex={addItemRowIndex}
        listItems={(itemcode, from, limit) => {
          // Update the item master with the newly created item
          fetchItemMaster(itemcode);
        }}
      />

      {/* {confirmModal.open && (
        <ConfirmModal
          open={confirmModal.open}
          onClose={() => {
            if (confirmModal.onCancel) {
              confirmModal.onCancel();
            } else {
              setConfirmModal(initialConfirmModalState);
            }
          }}
          onConfirm={confirmModal.onConfirm || handleOpeningBalance}
          title={confirmModal.title}
          content={confirmModal.content}
        />
      )} */}
    </Dialog>
  );
}

export default EnterOpeningStock;
